import { Slider, notTouchedGlowAnimation } from '@amelio/ui-library';
import {
    useEffect,
    useRef,
    useState,
    type CSSProperties,
    type KeyboardEvent,
    type MouseEvent as ReactMouseEvent,
    type PointerEvent as ReactPointerEvent,
} from 'react';

import { QuestionTextRenderer } from '@/components/QuestionTextRenderer/QuestionTextRenderer';

const NOT_TOUCHED_CLASSES = [
    '[&_[data-testid]:has(>_[role=slider])>div:not([role])]:hidden',
    '[&_[role=slider]]:[animation:var(--not-touched-glow-animation)]',
    '[&_[role=slider]]:bg-white',
].join(' ');

const NOT_TOUCHED_STYLE = {
    '--not-touched-glow-animation': notTouchedGlowAnimation,
} as CSSProperties;

/** The keys the slider answers to; any other key up is someone passing through. */
const COMMITTING_KEYS = new Set(['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End']);

export interface RatingScaleQuestionAnswerProps {
    /** Selected values (array with single value for consistency) */
    selectedValues?: string[];
    /** Fired when user selects a value */
    onChange: (values: string[]) => void;
    /**
     * Fired when a gesture that set a value ends, rather than for every value it passed through.
     * A touch on the scale reports a value straight away, so `onChange` alone cannot tell a
     * settled answer from a finger on its way somewhere.
     */
    onCommit?: () => void;
    onInteractionStart?: () => void;
    /** Minimum scale value (e.g. 1) */
    minValue?: number;
    /** Maximum scale value (e.g. 7 or 10) */
    maxValue?: number;
    /** Left label (e.g. Strongly disagree) */
    startLabel?: string;
    /** Right label (e.g. Strongly agree) */
    endLabel?: string;
    /** Disable interaction */
    disabled?: boolean;
    /** Optional class override */
    className?: string;
    /** Test id */
    dataTestId?: string;
}

/**
 * RatingScaleQuestionAnswer
 *
 * Survey answer component for rating-scale questions.
 * Delegates UI and accessibility to Slider.
 */
export const RatingScaleQuestionAnswer = ({
    selectedValues = [],
    onChange,
    onCommit,
    onInteractionStart,
    minValue = 1,
    maxValue = 7,
    startLabel = 'Low',
    endLabel = 'High',
    disabled = false,
    className = '',
    dataTestId = '',
}: RatingScaleQuestionAnswerProps) => {
    const defaultMiddle = Math.ceil((minValue + maxValue) / 2);
    const currentValue = selectedValues[0] ? parseInt(selectedValues[0], 10) : defaultMiddle;
    const [internalValue, setInternalValue] = useState<number>(currentValue);
    const [hasInteracted, setHasInteracted] = useState<boolean>(false);

    // Sync controlled value
    useEffect(() => {
        setInternalValue(currentValue);
    }, [currentValue]);

    const handleChange = (value: number) => {
        if (disabled) {
            return;
        }
        setInternalValue(value);
        onChange([String(value)]);
    };

    // The release happens wherever the pointer ended up -- the slider drags from `document` and
    // captures nothing -- so what it needs to know is read here rather than closed over on press.
    const latest = useRef({ selectedValues, internalValue, onChange, onCommit, disabled });
    useEffect(() => {
        latest.current = { selectedValues, internalValue, onChange, onCommit, disabled };
    });

    /**
     * A press that moved nothing still answers with the value under the finger: the slider only
     * reports a position that differs from the one it already shows, so without this the value on
     * screen could never be chosen. It waits for the release, so merely touching the scale -- or
     * starting a scroll on it -- is not an answer.
     */
    const answerWithShownValue = () => {
        const current = latest.current;
        if (current.disabled) {
            return;
        }
        if (!current.selectedValues.length) {
            current.onChange([String(current.internalValue)]);
        }
        current.onCommit?.();
    };

    const gestureRef = useRef(false);
    const endGestureRef = useRef<(() => void) | null>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => () => endGestureRef.current?.(), []);

    /**
     * The scale's own hit area, which is a strip inside a box that also holds the end labels, the
     * numbers and their padding. A press on any of that reaches this wrapper without reaching the
     * slider, and answering for it would put a value on a question nobody touched.
     */
    const landedOnScale = (target: EventTarget | null): boolean => {
        const track = wrapperRef.current?.querySelector('[role="slider"]')?.parentElement;
        return !!track && target instanceof Node && track.contains(target);
    };

    const handleSliderInteraction = (event: ReactPointerEvent<HTMLDivElement>) => {
        if (disabled || event.button !== 0 || !landedOnScale(event.target) || gestureRef.current) {
            return;
        }
        setHasInteracted(true);
        onInteractionStart?.();
        gestureRef.current = true;
        const pointerId = event.pointerId;

        const finish = (event: PointerEvent) => {
            if (event.pointerId !== pointerId) {
                return;
            }
            endGestureRef.current?.();
            if (event.type === 'pointercancel') {
                setHasInteracted(latest.current.selectedValues.length > 0);
            } else {
                answerWithShownValue();
            }
        };

        endGestureRef.current = () => {
            window.removeEventListener('pointerup', finish);
            window.removeEventListener('pointercancel', finish);
            endGestureRef.current = null;
            gestureRef.current = false;
        };

        window.addEventListener('pointerup', finish);
        window.addEventListener('pointercancel', finish);
    };

    const handleMouseDownCapture = (event: ReactMouseEvent<HTMLDivElement>) => {
        if (event.button !== 0) {
            event.stopPropagation();
        }
    };

    const isSliderKey = (event: KeyboardEvent<HTMLDivElement>): boolean =>
        event.target instanceof Element &&
        event.target.getAttribute('role') === 'slider' &&
        COMMITTING_KEYS.has(event.key);

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (!disabled && !gestureRef.current && isSliderKey(event)) {
            onInteractionStart?.();
        }
    };

    const handleKeyUp = (event: KeyboardEvent<HTMLDivElement>) => {
        if (!disabled && !gestureRef.current && isSliderKey(event)) {
            setHasInteracted(true);
            answerWithShownValue();
        }
    };

    const isNotTouched = !selectedValues.length && !hasInteracted;

    return (
        <div
            ref={wrapperRef}
            className={`w-full ${isNotTouched ? NOT_TOUCHED_CLASSES : ''} ${className}`}
            style={isNotTouched ? NOT_TOUCHED_STYLE : undefined}
            aria-disabled={disabled}
            role={'group'}
            onPointerDown={handleSliderInteraction}
            onMouseDownCapture={handleMouseDownCapture}
            onKeyDown={handleKeyDown}
            onKeyUp={handleKeyUp}
        >
            <Slider
                minValue={minValue}
                maxValue={maxValue}
                currentValue={internalValue}
                step={1}
                startLabel={startLabel ? <QuestionTextRenderer text={startLabel} /> : undefined}
                endLabel={endLabel ? <QuestionTextRenderer text={endLabel} /> : undefined}
                onChange={handleChange}
                showScale
                className={disabled ? 'pointer-events-none opacity-50' : ''}
                dataTestId={dataTestId}
            />
        </div>
    );
};

export default RatingScaleQuestionAnswer;
